import { Dispatch, SetStateAction, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import ProductPreview from '@/modules/catalogs/pages/catalog-product/product-details/ProductPreview'
import { useGetProductOutOfStockModal } from '@/modules/products/components/out-of-stock/hooks/useGetProductOutOfStockModal'
import { Button } from '@/modules/shared/components/button/Button'
import { Modal } from '@/modules/shared/components/modal/ModalComponent'
import { BooleanItem } from '@/modules/shared/components/table/table-column-items/shared/BooleanItem'
import { TileDetailsTableItem } from '@/modules/shared/components/tile/tile-items/TileDetailsTableItem'

interface ProductOutOfStockModalProps {
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
  productId: number
}

export default function ProductOutOfStockModal({ showModal, setShowModal, productId }: ProductOutOfStockModalProps) {
  const { t } = useTranslation()

  const { fetchProductOutOfStockModal, product, loadingStates, error } = useGetProductOutOfStockModal({ productId })

  const onCloseModal = () => {
    setShowModal(false)
  }

  useEffect(() => {
    if (showModal) {
      fetchProductOutOfStockModal()
    }
  }, [showModal])

  return (
    <Modal showModal={showModal} onCloseModal={onCloseModal} loading={loadingStates.loading} error={error}>
      <Modal.Panel className="flex w-full flex-col overflow-hidden rounded-md bg-white shadow-xl transition-all md:max-w-[37.5rem]">
        <Modal.Title title={t('general.outOfStock', 'Out of Stock')} onCloseModal={onCloseModal} />
        <Modal.Body className="overflow-y-scroll">
          <ProductPreview product={product || null} />
          <div className="mt-5">
            <p className="font-bold">{t('general.outOfStock', 'Out of Stock')}</p>
            <p className="text-sm text-gray-500">
              {t(
                'outOfStockModal.description',
                'The Owner of this product {{ productOwner }} has marked this product as Out of Stock. The product can still be included on a Requisition but it is recommended that the Owner of the product be contacted to check the availability of the product.',
                { productOwner: product?.owner?.name }
              )}
            </p>
            <div className="mb-4 mt-2 border">
              <TileDetailsTableItem
                title={t('general.p+ProductCode', 'P+ Product Code')}
                value={product?.id}
                tooltip={t(
                  'shopPage.productModal.productDetails.p+CodeDescription',
                  'The unique Code that is used to identify this product in the PurchasePlus system.'
                )}
              />
              <TileDetailsTableItem
                title={t('general.p+MasterCode', 'P+ Master Code')}
                value={product?.canonicalProductId}
                tooltip={t(
                  'shopPage.productModal.productDetails.p+MasterCodeDescription',
                  'If the product is a Master Product or is matched to a Master product, the unique code of the P+ Master product will be shown here.'
                )}
              />
              <TileDetailsTableItem
                title={t('general.owner', 'Owner')}
                value={product?.owner?.name}
                tooltip={t(
                  'shopPage.productModal.productDetails.ownerDescription',
                  'Indicates if the Product is owned and managed by your Organisation, owned and managed by a Supplier, or a Master product owned and managed by PurchasePlus.'
                )}
              />
              <TileDetailsTableItem
                title={t('general.outOfStock', 'Out of Stock')}
                value={<BooleanItem value={!!product?.outOfStock} />}
                tooltip={t(
                  'shopPage.productModal.productDetails.outOfStockDescription',
                  'Indicates if the product is currently out of Stock. This can be useful for Suppliers to communicate that the product is temporarily or permanently unavailable. A Purchaser is not prevented from adding an Out of Stock product to a Requisition.'
                )}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex flex-col justify-end gap-2 md:flex-row md:items-center">
          <Button className="h-11 w-full rounded-md bg-gray-200 px-5 text-sm md:w-fit" onClick={onCloseModal}>
            {t('general.close', 'Close')}
          </Button>
        </Modal.Footer>
      </Modal.Panel>
    </Modal>
  )
}
